import { createContext, useContext } from 'react'

import type { AlertsCount, RouteAlert } from '../../pages/spot-search/model/types'

// Licznik nowych ofert zapisanych tras (spec §11.5, D38): jedno źródło danych dla menu i kart tras.

export interface SpotSearchAlertsValue {
  counts: AlertsCount | null
  /** Suma nowych ofert tras w stanie OK; PAUSED/STALE/SEEDING nie dają badge'a (nigdy „0” bez sprawdzenia). */
  badgeCount: number
  /** Trasa z największą liczbą nowych (remis: świeższe sprawdzenie) — cel kliknięcia „Szukaj fracht”. */
  topRouteId: number | null
  byRoute: ReadonlyMap<number, RouteAlert>
  refresh: () => void
}

export const summarizeAlerts = (
  counts: AlertsCount | null
): Pick<SpotSearchAlertsValue, 'badgeCount' | 'topRouteId' | 'byRoute'> => {
  const routes = counts?.routes ?? []
  const counted = routes.filter((route) => route.state === 'OK' && route.newCount > 0)
  const top = counted.reduce<RouteAlert | null>((best, route) => {
    if (!best || route.newCount > best.newCount) {
      return route
    }
    if (route.newCount === best.newCount && (route.lastCheckedAt ?? '') > (best.lastCheckedAt ?? '')) {
      return route
    }
    return best
  }, null)
  return {
    badgeCount: counted.reduce((sum, route) => sum + route.newCount, 0),
    topRouteId: top?.routeId ?? null,
    byRoute: new Map(routes.map((route) => [route.routeId, route])),
  }
}

export const defaultSpotSearchAlertsValue: SpotSearchAlertsValue = {
  counts: null,
  badgeCount: 0,
  topRouteId: null,
  byRoute: new Map(),
  refresh: () => undefined,
}

export const SpotSearchAlertsContext = createContext<SpotSearchAlertsValue>(defaultSpotSearchAlertsValue)

export const useSpotSearchAlerts = (): SpotSearchAlertsValue => useContext(SpotSearchAlertsContext)
